import React, { useEffect, useRef, useState } from 'react';
import { InputAdornment } from '@mui/material';

import {
  CustomInputContainer,
  Label,
  StyledIconButton,
  StyledTextField,
} from './styles';
import SaveIcon from '../../../../assets/icons/SaveIcon';
import EditIcon from '../../../../assets/icons/EditIcon';
import { CustomInputProps } from './types';

const CustomInput: React.FC<CustomInputProps> = ({
  label,
  value,
  onSave,
  isEditInput = true,
  readonly = false,
  editByExternalLink = false,
  handleLinkTo,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handleInputClick = () => {
    if (!isEditing && !readonly && !editByExternalLink) {
      setIsEditing(true);
      if (inputRef.current) {
        const inputLength = inputRef.current.value.length;
        inputRef.current.setSelectionRange(inputLength, inputLength);
        inputRef.current.focus();
      }
    }
  };

  const handleSaveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    const trimmedValue = currentValue.trim();
    setCurrentValue(trimmedValue);
    onSave &&
      onSave({
        target: { value: trimmedValue },
      } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleBlur = () => {
    if (isEditing) {
      setIsEditing(false);
    }
  };

  const handleExternalLinkClick = () => {
    if (handleLinkTo) {
      handleLinkTo();
    }
  };

  const isChanged = currentValue.trim() !== value.trim();

  return (
    <CustomInputContainer isEditing={isEditing} isChanged={isChanged}>
      <Label>{label}</Label>
      <StyledTextField
        inputRef={inputRef}
        value={currentValue}
        onChange={(e) => setCurrentValue(e.target.value)}
        variant="outlined"
        fullWidth
        onClick={handleInputClick}
        onBlur={handleBlur}
        multiline
        InputProps={{
          endAdornment: isEditInput && !readonly && (
            <InputAdornment position="end">
              <StyledIconButton
                onClick={
                  editByExternalLink
                    ? handleExternalLinkClick
                    : isChanged
                      ? handleSaveClick
                      : handleInputClick
                }
                disabled={readonly}
              >
                {editByExternalLink ? (
                  <EditIcon />
                ) : isChanged ? (
                  <SaveIcon />
                ) : (
                  <EditIcon />
                )}
              </StyledIconButton>
            </InputAdornment>
          ),
          readOnly: readonly || !isEditing,
          disabled: readonly || editByExternalLink,
        }}
        sx={{
          '.MuiOutlinedInput-notchedOutline': readonly
            ? { border: 'none' }
            : {},
          '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
            readonly ? { border: 'none' } : {},
        }}
      />
    </CustomInputContainer>
  );
};

export default CustomInput;
